import { useCallback, useRef } from 'react';

export const BOTTOM_BAR_INSET_VAR = '--BottomBar_inset';

/**
 * Space the BottomNavigationBar covers above the viewport bottom, for content fixed to that same
 * bottom edge. Published by the mounted bar; until then the --BottomBar_height token stands in.
 * It differs from the token: the bar's em padding outgrows its set height, and the raised
 * centre action reaches past the bar box by an amount that changes with the label size.
 */
export const bottomBarInset = `var(${BOTTOM_BAR_INSET_VAR}, var(--BottomBar_height))`;

/**
 * Distance from the bar's bottom edge to the top of its highest painted descendant.
 * @param {HTMLElement} nav - The BottomNavigation root
 * @returns {number} Zero while the bar is hidden for the keyboard.
 */
export const measureBottomBarInset = (nav) => {
  const navRect = nav.getBoundingClientRect();
  if (navRect.height === 0) return 0;

  // Collapsed elements, like an invisible badge, report a meaningless top.
  const tops = Array.from(nav.querySelectorAll('*'), (el) => el.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .map((rect) => rect.top);
  return navRect.bottom - Math.min(navRect.top, ...tops);
};

/**
 * Callback ref for the bar root that keeps --BottomBar_inset on the document root up to date.
 * A ref instead of an effect: the bar renders through a Portal, so it attaches after the
 * parent's effects have run, and never on the server.
 * @returns {(nav: HTMLElement | null) => void}
 */
export const useBottomBarInsetRef = () => {
  const cleanupRef = useRef(null);

  return useCallback((nav) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!nav) return;

    const rootStyle = document.documentElement.style;
    const publish = () => {
      rootStyle.setProperty(BOTTOM_BAR_INSET_VAR, `${measureBottomBarInset(nav)}px`);
    };
    publish();

    // The bar box resizes with the width (labels grow at 460px, rotation) and when the keyboard
    // hides it. Safari before 13.1 lacks ResizeObserver, window resizes cover the width there.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publish);
      cleanupRef.current = () => {
        window.removeEventListener('resize', publish);
        rootStyle.removeProperty(BOTTOM_BAR_INSET_VAR);
      };
      return;
    }
    const observer = new ResizeObserver(publish);
    observer.observe(nav);
    cleanupRef.current = () => {
      observer.disconnect();
      rootStyle.removeProperty(BOTTOM_BAR_INSET_VAR);
    };
  }, []);
};
